// Hand-built Iznik-style decorative SVGs (tulip rosette + corner border)
// Kept as pure SVG so colours stay crisp at any scale.
import { IMAGES } from "./config";

// Repeating Iznik tile texture overlay — sits behind section content.
// Use `dark` on turquoise/ink backgrounds for a subtle glow via blend mode.
export function IznikTexture({ opacity, dark = false, size = 300, className = "" }) {
  const o = opacity ?? (dark ? 0.09 : 0.04);
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 bg-repeat ${
        dark ? "mix-blend-soft-light" : "mix-blend-multiply"
      } ${className}`}
      style={{
        backgroundImage: `url(${IMAGES.iznik})`,
        backgroundSize: `${size}px`,
        opacity: o,
      }}
    />
  );
}

export function IznikRosette({ className = "", color = "#0F7A93", accent = "#C5A059" }) {
  return (
    <svg
      viewBox="0 0 200 200"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g stroke={color} strokeWidth="1.4">
        {[...Array(8)].map((_, i) => (
          <g key={i} transform={`rotate(${i * 45} 100 100)`}>
            <path
              d="M100 100 C 92 70, 92 46, 100 26 C 108 46, 108 70, 100 100 Z"
              fill={i % 2 === 0 ? "rgba(21,168,200,0.14)" : "rgba(255,94,142,0.12)"}
            />
            <circle cx="100" cy="30" r="3.2" fill={accent} stroke="none" />
          </g>
        ))}
        <circle cx="100" cy="100" r="16" fill="rgba(197,160,89,0.18)" />
        <circle cx="100" cy="100" r="7" fill={accent} stroke="none" />
        <circle cx="100" cy="100" r="46" strokeDasharray="2 6" />
      </g>
    </svg>
  );
}

// A single Iznik tulip — the classic Ottoman motif
export function IznikTulip({ className = "", color = "#0F7A93", accent = "#FF5E8E" }) {
  return (
    <svg
      viewBox="0 0 100 130"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M50 122 C 50 100, 50 80, 50 62"
        stroke={color}
        strokeWidth="1.6"
      />
      <path
        d="M50 62 C 30 62, 20 44, 24 20 C 34 34, 44 34, 50 22 C 56 34, 66 34, 76 20 C 80 44, 70 62, 50 62 Z"
        fill="rgba(21,168,200,0.16)"
        stroke={color}
        strokeWidth="1.6"
      />
      <path d="M50 22 C 50 34, 50 48, 50 60" stroke={accent} strokeWidth="1.4" />
      <path
        d="M50 84 C 66 82, 76 92, 78 104 C 66 104, 54 98, 50 84 Z"
        fill="rgba(197,160,89,0.16)"
        stroke={color}
        strokeWidth="1.4"
      />
      <path
        d="M50 96 C 34 94, 24 104, 22 116 C 34 116, 46 110, 50 96 Z"
        fill="rgba(232,180,184,0.2)"
        stroke={color}
        strokeWidth="1.4"
      />
    </svg>
  );
}

// Thin ornamental divider
export function OrnamentDivider({ className = "", color = "#C5A059" }) {
  return (
    <svg
      viewBox="0 0 240 24"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <line x1="0" y1="12" x2="92" y2="12" stroke={color} strokeWidth="1" />
      <line x1="148" y1="12" x2="240" y2="12" stroke={color} strokeWidth="1" />
      <g transform="translate(120 12)" stroke={color} strokeWidth="1.2">
        <path d="M0 -10 C 5 -4, 5 4, 0 10 C -5 4, -5 -4, 0 -10 Z" fill="rgba(197,160,89,0.25)" />
        <circle cx="0" cy="0" r="2.2" fill={color} stroke="none" />
        <path d="M-18 0 L -8 0" />
        <path d="M8 0 L 18 0" />
        <circle cx="-20" cy="0" r="1.6" fill={color} stroke="none" />
        <circle cx="20" cy="0" r="1.6" fill={color} stroke="none" />
      </g>
    </svg>
  );
}

// Gold corner ornament (rotate via className for each corner)
export function CornerOrnament({ className = "", color = "#C5A059" }) {
  return (
    <svg
      viewBox="0 0 80 80"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M4 40 L4 4 L40 4" stroke={color} strokeWidth="1.2" />
      <path d="M12 40 C 12 20, 20 12, 40 12" stroke={color} strokeWidth="1" />
      <path
        d="M14 14 C 22 14, 30 8, 30 8 C 30 16, 22 22, 14 22 C 14 18, 14 16, 14 14 Z"
        fill="rgba(197,160,89,0.22)"
        stroke={color}
        strokeWidth="0.8"
      />
      <circle cx="4" cy="4" r="2" fill={color} />
    </svg>
  );
}

// Simple petal shape for drifting particles
export function Petal({ className = "", color = "#FF5E8E" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 2 C 17 7, 17 15, 12 22 C 7 15, 7 7, 12 2 Z"
        fill={color}
        opacity="0.7"
      />
      <path d="M12 4 C 12 10, 12 16, 12 20" stroke="rgba(255,255,255,0.5)" strokeWidth="0.6" />
    </svg>
  );
}